import React, { useEffect, useRef } from 'react';

export default function FluidCursor() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let frameId: number;

    try {
      /* ─────────────────── CONFIG ─────────────────── */
      const config = {
        SIM_RESOLUTION:      128,
        DYE_RESOLUTION:      1024,
        DENSITY_DISSIPATION: 4.0,
        VELOCITY_DISSIPATION: 2.5,
        PRESSURE:            0.15,
        PRESSURE_ITERATIONS: 20,
        CURL:                6,
        SPLAT_RADIUS:        0.04,
        SPLAT_FORCE:         4000,
        SHADING:             true,
        COLOR_UPDATE_SPEED:  4,
      };

      /* ─────────────────── PRIMARY COLORS ─────────────────── */
      const primaryPalette = [
        { h: 0.48, s: 1.0, v: 1.0 },   // Intense Cyan
        { h: 0.50, s: 0.8, v: 1.0 },   // Bright Teal
        { h: 0.45, s: 1.0, v: 1.0 },   // Deep Cyan
        { h: 0.48, s: 0.5, v: 1.0 },   // Hot White-Cyan
        { h: 0.52, s: 0.9, v: 1.0 },   // Azure
      ];

      function HSVtoRGB(h: number, s: number, v: number) {
        let r = 0, g = 0, b = 0;
        const i = Math.floor(h * 6), f = h * 6 - i,
              p = v * (1 - s), q = v * (1 - f * s), t = v * (1 - (1 - f) * s);
        switch (i % 6) {
          case 0: r=v; g=t; b=p; break; case 1: r=q; g=v; b=p; break;
          case 2: r=p; g=v; b=t; break; case 3: r=p; g=q; b=v; break;
          case 4: r=t; g=p; b=v; break; case 5: r=v; g=p; b=q; break;
        }
        return { r, g, b };
      }

      function generateFireColor() {
        const pick = primaryPalette[Math.floor(Math.random() * primaryPalette.length)];
        const c = HSVtoRGB(pick.h + (Math.random() - 0.5) * 0.02, pick.s, pick.v);
        // Made the intensity significantly lighter so it's less "heavy/dense"
        const intensity = 0.05 + Math.random() * 0.05;
        return { r: c.r * intensity, g: c.g * intensity, b: c.b * intensity };
      }

      /* ─────────────────── POINTER ─────────────────── */
      function PointerPrototype(this: any) {
        this.id = -1; this.texcoordX = 0; this.texcoordY = 0;
        this.prevTexcoordX = 0; this.prevTexcoordY = 0;
        this.deltaX = 0; this.deltaY = 0;
        this.down = false; this.moved = false; this.color = { r:0, g:0, b:0 };
      }
      //@ts-ignore
      let pointers = [new PointerPrototype()];

      /* ─────────────────── WEBGL SETUP ─────────────────── */
      function getWebGLContext(c: HTMLCanvasElement) {
        const params = { alpha:true, depth:false, stencil:false, antialias:false, preserveDrawingBuffer:false };
        let gl = c.getContext('webgl2', params) as any;
        const isWebGL2 = !!gl;
        if (!isWebGL2) gl = c.getContext('webgl', params) || c.getContext('experimental-webgl', params);
        
        if (!gl) return null;

        let halfFloat, supportLinearFiltering;
        if (isWebGL2) {
          gl.getExtension('EXT_color_buffer_float');
          supportLinearFiltering = gl.getExtension('OES_texture_float_linear');
        } else {
          halfFloat = gl.getExtension('OES_texture_half_float');
          supportLinearFiltering = gl.getExtension('OES_texture_half_float_linear');
        }
        gl.clearColor(0, 0, 0, 1);
        const hfType = isWebGL2 ? gl.HALF_FLOAT : (halfFloat && halfFloat.HALF_FLOAT_OES);
        const sf = (iF: any, f: any) => getSupportedFormat(gl, iF, f, hfType);
        let fRGBA, fRG, fR;
        if (isWebGL2) {
          fRGBA = sf(gl.RGBA16F, gl.RGBA); fRG = sf(gl.RG16F, gl.RG); fR = sf(gl.R16F, gl.RED);
        } else {
          fRGBA = sf(gl.RGBA, gl.RGBA);   fRG  = sf(gl.RGBA, gl.RGBA); fR = sf(gl.RGBA, gl.RGBA);
        }
        return { gl, ext: { fRGBA, fRG, fR, halfFloatTexType: hfType, supportLinearFiltering } };
      }

      function getSupportedFormat(gl: any, iF: any, f: any, t: any) {
        if (!canRenderTo(gl, iF, f, t)) {
          if (iF === gl.R16F)  return getSupportedFormat(gl, gl.RG16F,   gl.RG,   t);
          if (iF === gl.RG16F) return getSupportedFormat(gl, gl.RGBA16F, gl.RGBA, t);
          return null;
        }
        return { internalFormat: iF, format: f };
      }

      function canRenderTo(gl: any, iF: any, f: any, t: any) {
        const tex = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texImage2D(gl.TEXTURE_2D, 0, iF, 4, 4, 0, f, t, null);
        const fbo = gl.createFramebuffer(); gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
        return gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
      }

      const context = getWebGLContext(canvas);
      if (!context) return;
      const { gl, ext } = context;

      if (!ext.supportLinearFiltering) { config.DYE_RESOLUTION = 256; config.SHADING = false; }

      /* ─────────────────── SHADER HELPERS ─────────────────── */
      function compileShader(type: number, src: string, kw?: string[]) {
        if (kw) src = kw.map(k => `#define ${k}\n`).join('') + src;
        const s = gl.createShader(type);
        gl.shaderSource(s, src); gl.compileShader(s);
        if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) console.warn(gl.getShaderInfoLog(s));
        return s;
      }
      function createProgram(vs: WebGLShader, fs: WebGLShader) {
        const p = gl.createProgram();
        gl.attachShader(p, vs); gl.attachShader(p, fs); gl.linkProgram(p); return p;
      }
      function getUniforms(prog: WebGLProgram) {
        const u: any = {}, n = gl.getProgramParameter(prog, gl.ACTIVE_UNIFORMS);
        for (let i = 0; i < n; i++) { const nm = gl.getActiveUniform(prog, i).name; u[nm] = gl.getUniformLocation(prog, nm); }
        return u;
      }

      class Prog {
        program: WebGLProgram;
        uniforms: any;
        constructor(vs: WebGLShader, fs: WebGLShader) { this.program = createProgram(vs, fs); this.uniforms = getUniforms(this.program); }
        bind() { gl.useProgram(this.program); }
      }
      class Mat {
        vs: WebGLShader;
        src: string;
        progs: any;
        active: WebGLProgram | null;
        uniforms: any;
        constructor(vs: WebGLShader, src: string) { this.vs = vs; this.src = src; this.progs = {}; this.active = null; this.uniforms = {}; }
        setKW(kw: string[]) {
          const h = kw.join('|');
          if (!this.progs[h]) { const fs = compileShader(gl.FRAGMENT_SHADER, this.src, kw); this.progs[h] = createProgram(this.vs, fs); }
          if (this.progs[h] === this.active) return;
          this.active = this.progs[h]; this.uniforms = getUniforms(this.active);
        }
        bind() { gl.useProgram(this.active); }
      }

      /* ─────────────────── SHADERS ─────────────────── */
      const baseVS = compileShader(gl.VERTEX_SHADER, `
        precision highp float;
        attribute vec2 aPosition;
        varying vec2 vUv, vL, vR, vT, vB;
        uniform vec2 texelSize;
        void main() {
          vUv = aPosition * 0.5 + 0.5;
          vL = vUv - vec2(texelSize.x, 0.0);
          vR = vUv + vec2(texelSize.x, 0.0);
          vT = vUv + vec2(0.0, texelSize.y);
          vB = vUv - vec2(0.0, texelSize.y);
          gl_Position = vec4(aPosition, 0.0, 1.0);
        }`);

      const clearP = new Prog(baseVS, compileShader(gl.FRAGMENT_SHADER, `precision mediump float; precision mediump sampler2D; varying highp vec2 vUv; uniform sampler2D uTexture; uniform float value; void main(){gl_FragColor=value*texture2D(uTexture,vUv);}`));
      const splatP = new Prog(baseVS, compileShader(gl.FRAGMENT_SHADER, `
        precision highp float; precision highp sampler2D;
        varying vec2 vUv; uniform sampler2D uTarget;
        uniform float aspectRatio; uniform vec3 color; uniform vec2 point; uniform float radius;
        void main(){
          vec2 p = vUv - point.xy; p.x *= aspectRatio;
          vec3 splat = exp(-dot(p,p)/radius)*color;
          vec3 base = texture2D(uTarget,vUv).xyz;
          gl_FragColor = vec4(base+splat,1.0);
        }`));
      const advP  = new Prog(baseVS, compileShader(gl.FRAGMENT_SHADER, `
        precision highp float; precision highp sampler2D;
        varying vec2 vUv; uniform sampler2D uVelocity, uSource;
        uniform vec2 texelSize, dyeTexelSize; uniform float dt, dissipation;
        void main(){
          vec2 coord = vUv - dt*texture2D(uVelocity,vUv).xy*texelSize;
          vec4 result = texture2D(uSource,coord);
          gl_FragColor = result / (1.0 + dissipation*dt);
        }`));
      const divP  = new Prog(baseVS, compileShader(gl.FRAGMENT_SHADER, `
        precision mediump float; precision mediump sampler2D;
        varying highp vec2 vUv,vL,vR,vT,vB; uniform sampler2D uVelocity;
        void main(){
          float L=texture2D(uVelocity,vL).x, R=texture2D(uVelocity,vR).x,
                T=texture2D(uVelocity,vT).y, B=texture2D(uVelocity,vB).y;
          vec2 C=texture2D(uVelocity,vUv).xy;
          if(vL.x<0.0)L=-C.x; if(vR.x>1.0)R=-C.x;
          if(vT.y>1.0)T=-C.y; if(vB.y<0.0)B=-C.y;
          gl_FragColor=vec4(0.5*(R-L+T-B),0.0,0.0,1.0);
        }`));
      const curlP = new Prog(baseVS, compileShader(gl.FRAGMENT_SHADER, `
        precision mediump float; precision mediump sampler2D;
        varying highp vec2 vUv,vL,vR,vT,vB; uniform sampler2D uVelocity;
        void main(){
          float L=texture2D(uVelocity,vL).y, R=texture2D(uVelocity,vR).y,
                T=texture2D(uVelocity,vT).x, B=texture2D(uVelocity,vB).x;
          gl_FragColor=vec4(0.5*(R-L-T+B),0.0,0.0,1.0);
        }`));
      const vortP = new Prog(baseVS, compileShader(gl.FRAGMENT_SHADER, `
        precision highp float; precision highp sampler2D;
        varying vec2 vUv,vL,vR,vT,vB; uniform sampler2D uVelocity, uCurl;
        uniform float curl, dt;
        void main(){
          float L=texture2D(uCurl,vL).x, R=texture2D(uCurl,vR).x,
                T=texture2D(uCurl,vT).x, B=texture2D(uCurl,vB).x, C=texture2D(uCurl,vUv).x;
          vec2 force=0.5*vec2(abs(T)-abs(B),abs(R)-abs(L));
          force/=length(force)+0.0001; force*=curl*C; force.y*=-1.0;
          vec2 vel=texture2D(uVelocity,vUv).xy+force*dt;
          gl_FragColor=vec4(clamp(vel,-1000.0,1000.0),0.0,1.0);
        }`));
      const pressP = new Prog(baseVS, compileShader(gl.FRAGMENT_SHADER, `
        precision mediump float; precision mediump sampler2D;
        varying highp vec2 vUv,vL,vR,vT,vB; uniform sampler2D uPressure, uDivergence;
        void main(){
          float L=texture2D(uPressure,vL).x, R=texture2D(uPressure,vR).x,
                T=texture2D(uPressure,vT).x, B=texture2D(uPressure,vB).x,
                div=texture2D(uDivergence,vUv).x;
          gl_FragColor=vec4((L+R+B+T-div)*0.25,0.0,0.0,1.0);
        }`));
      const gradP = new Prog(baseVS, compileShader(gl.FRAGMENT_SHADER, `
        precision mediump float; precision mediump sampler2D;
        varying highp vec2 vUv,vL,vR,vT,vB; uniform sampler2D uPressure, uVelocity;
        void main(){
          float L=texture2D(uPressure,vL).x, R=texture2D(uPressure,vR).x,
                T=texture2D(uPressure,vT).x, B=texture2D(uPressure,vB).x;
          vec2 vel=texture2D(uVelocity,vUv).xy-vec2(R-L,T-B);
          gl_FragColor=vec4(vel,0.0,1.0);
        }`));
      const dispM = new Mat(baseVS, `
        precision highp float; precision highp sampler2D;
        varying vec2 vUv,vL,vR,vT,vB; uniform sampler2D uTexture; uniform vec2 texelSize;
        void main(){
          vec3 c=texture2D(uTexture,vUv).rgb;
          #ifdef SHADING
          vec3 lc=texture2D(uTexture,vL).rgb, rc=texture2D(uTexture,vR).rgb,
               tc=texture2D(uTexture,vT).rgb, bc=texture2D(uTexture,vB).rgb;
          float dx=length(rc)-length(lc), dy=length(tc)-length(bc);
          vec3 n=normalize(vec3(dx,dy,length(texelSize)));
          c *= clamp(dot(n,vec3(0,0,1))+0.7, 0.7, 1.0);
          #endif
          gl_FragColor=vec4(c, max(c.r,max(c.g,c.b)));
        }`);

      //@ts-ignore
      dispM.setKW(config.SHADING ? ['SHADING'] : []);

      /* ─────────────────── BUFFERS ─────────────────── */
      gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1,-1,1,1,1,1,-1]), gl.STATIC_DRAW);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, gl.createBuffer());
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array([0,1,2,0,2,3]), gl.STATIC_DRAW);
      gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
      gl.enableVertexAttribArray(0);

      const blit = (tgt: any, clear = false) => {
        if (tgt == null) { gl.viewport(0,0,gl.drawingBufferWidth,gl.drawingBufferHeight); gl.bindFramebuffer(gl.FRAMEBUFFER,null); }
        else             { gl.viewport(0,0,tgt.width,tgt.height); gl.bindFramebuffer(gl.FRAMEBUFFER,tgt.fbo); }
        if (clear) { gl.clearColor(0,0,0,1); gl.clear(gl.COLOR_BUFFER_BIT); }
        gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
      };

      /* ─────────────────── FRAMEBUFFERS ─────────────────── */
      function mkFBO(w: number, h: number, iF: any, f: any, t: any, p: any) {
        gl.activeTexture(gl.TEXTURE0);
        const tex = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, p);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, p);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texImage2D(gl.TEXTURE_2D, 0, iF, w, h, 0, f, t, null);
        const fbo = gl.createFramebuffer(); gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
        gl.viewport(0,0,w,h); gl.clear(gl.COLOR_BUFFER_BIT);
        return { texture:tex, fbo, width:w, height:h, texelSizeX:1/w, texelSizeY:1/h,
          attach(id: number) { gl.activeTexture(gl.TEXTURE0+id); gl.bindTexture(gl.TEXTURE_2D,tex); return id; } };
      }
      function mkDFBO(w: number, h: number, iF: any, f: any, t: any, p: any) {
        let f1 = mkFBO(w,h,iF,f,t,p), f2 = mkFBO(w,h,iF,f,t,p);
        return { width:w, height:h, texelSizeX:f1.texelSizeX, texelSizeY:f1.texelSizeY,
          get read(){return f1;}, set read(v){f1=v;},
          get write(){return f2;}, set write(v){f2=v;},
          swap(){let tmp=f1;f1=f2;f2=tmp;} };
      }

      let dye: any, vel: any, div: any, curl: any, press: any;
      function getRes(r: number) {
        let ar = gl.drawingBufferWidth / gl.drawingBufferHeight; if (ar<1) ar = 1/ar;
        const mn = Math.round(r), mx = Math.round(r*ar);
        return gl.drawingBufferWidth > gl.drawingBufferHeight ? {width:mx,height:mn} : {width:mn,height:mx};
      }
      function initFBOs() {
        const sR=getRes(config.SIM_RESOLUTION), dR=getRes(config.DYE_RESOLUTION);
        const tt=ext.halfFloatTexType, rgba=ext.fRGBA, rg=ext.fRG, r=ext.fR;
        const fi = ext.supportLinearFiltering ? gl.LINEAR : gl.NEAREST;
        gl.disable(gl.BLEND);
        if (!dye) dye  = mkDFBO(dR.width,dR.height,rgba.internalFormat,rgba.format,tt,fi);
        if (!vel) vel  = mkDFBO(sR.width,sR.height,rg.internalFormat,  rg.format,  tt,fi);
        div  = mkFBO(sR.width,sR.height,r.internalFormat,r.format,tt,gl.NEAREST);
        curl = mkFBO(sR.width,sR.height,r.internalFormat,r.format,tt,gl.NEAREST);
        press= mkDFBO(sR.width,sR.height,r.internalFormat,r.format,tt,gl.NEAREST);
      }

      function scl(v: number) { return Math.floor(v*(window.devicePixelRatio||1)); }
      function resizeCanvas() {
        const w=scl(window.innerWidth), h=scl(window.innerHeight);
        if (canvas.width!==w||canvas.height!==h) { canvas.width=w; canvas.height=h; return true; } return false;
      }
      function crX(d: number){const ar=canvas.width/canvas.height; if(ar<1)d*=ar; return d;}
      function crY(d: number){const ar=canvas.width/canvas.height; if(ar>1)d/=ar; return d;}
      function crR(r: number){const ar=canvas.width/canvas.height; if(ar>1)r*=ar; return r;}

      /* ─────────────────── SPLAT ─────────────────── */
      function splat(x: number, y: number, dx: number, dy: number, col: any) {
        splatP.bind();
        gl.uniform1i(splatP.uniforms.uTarget,   vel.read.attach(0));
        gl.uniform1f(splatP.uniforms.aspectRatio, canvas.width/canvas.height);
        gl.uniform2f(splatP.uniforms.point,  x, y);
        gl.uniform3f(splatP.uniforms.color,  dx, dy, 0);
        gl.uniform1f(splatP.uniforms.radius, crR(config.SPLAT_RADIUS/100));
        blit(vel.write); vel.swap();
        gl.uniform1i(splatP.uniforms.uTarget, dye.read.attach(0));
        gl.uniform3f(splatP.uniforms.color,   col.r, col.g, col.b);
        blit(dye.write); dye.swap();
      }
      function splatPtr(p: any) { splat(p.texcoordX,p.texcoordY,p.deltaX*config.SPLAT_FORCE,p.deltaY*config.SPLAT_FORCE,p.color); }
      function clickSplat(p: any) {
        const c=generateFireColor(); c.r*=8; c.g*=8; c.b*=8; // reduced multiplier
        splat(p.texcoordX,p.texcoordY,600*(Math.random()-0.5),600*(Math.random()-0.5),c);
      }

      /* ─────────────────── POINTER UTILS ─────────────────── */
      function updateDown(ptr: any,id: any,px: number,py: number) {
        ptr.id=id; ptr.down=true; ptr.moved=false;
        ptr.texcoordX=px/canvas.width; ptr.texcoordY=1-(py/canvas.height);
        ptr.prevTexcoordX=ptr.texcoordX; ptr.prevTexcoordY=ptr.texcoordY;
        ptr.deltaX=0; ptr.deltaY=0; ptr.color=generateFireColor();
      }
      function updateMove(ptr: any,px: number,py: number) {
        ptr.prevTexcoordX=ptr.texcoordX; ptr.prevTexcoordY=ptr.texcoordY;
        ptr.texcoordX=px/canvas.width;  ptr.texcoordY=1-(py/canvas.height);
        ptr.deltaX=crX(ptr.texcoordX-ptr.prevTexcoordX);
        ptr.deltaY=crY(ptr.texcoordY-ptr.prevTexcoordY);
        ptr.moved=Math.abs(ptr.deltaX)>0||Math.abs(ptr.deltaY)>0;
      }

      /* ─────────────────── SIMULATION STEP ─────────────────── */
      function step(dt: number) {
        gl.disable(gl.BLEND);
        curlP.bind();
        gl.uniform2f(curlP.uniforms.texelSize,vel.texelSizeX,vel.texelSizeY);
        gl.uniform1i(curlP.uniforms.uVelocity,vel.read.attach(0)); blit(curl);
        vortP.bind();
        gl.uniform2f(vortP.uniforms.texelSize,vel.texelSizeX,vel.texelSizeY);
        gl.uniform1i(vortP.uniforms.uVelocity,vel.read.attach(0));
        gl.uniform1i(vortP.uniforms.uCurl,curl.attach(1));
        gl.uniform1f(vortP.uniforms.curl,config.CURL);
        gl.uniform1f(vortP.uniforms.dt,dt);
        blit(vel.write); vel.swap();
        divP.bind();
        gl.uniform2f(divP.uniforms.texelSize,vel.texelSizeX,vel.texelSizeY);
        gl.uniform1i(divP.uniforms.uVelocity,vel.read.attach(0)); blit(div);
        clearP.bind();
        gl.uniform1i(clearP.uniforms.uTexture,press.read.attach(0));
        gl.uniform1f(clearP.uniforms.value,config.PRESSURE); blit(press.write); press.swap();
        pressP.bind();
        gl.uniform2f(pressP.uniforms.texelSize,vel.texelSizeX,vel.texelSizeY);
        gl.uniform1i(pressP.uniforms.uDivergence,div.attach(0));
        for (let i=0;i<config.PRESSURE_ITERATIONS;i++) {
          gl.uniform1i(pressP.uniforms.uPressure,press.read.attach(1)); blit(press.write); press.swap();
        }
        gradP.bind();
        gl.uniform2f(gradP.uniforms.texelSize,vel.texelSizeX,vel.texelSizeY);
        gl.uniform1i(gradP.uniforms.uPressure,press.read.attach(0));
        gl.uniform1i(gradP.uniforms.uVelocity,vel.read.attach(1)); blit(vel.write); vel.swap();
        advP.bind();
        gl.uniform2f(advP.uniforms.texelSize,vel.texelSizeX,vel.texelSizeY);
        gl.uniform2f(advP.uniforms.dyeTexelSize,vel.texelSizeX,vel.texelSizeY);
        const vId=vel.read.attach(0);
        gl.uniform1i(advP.uniforms.uVelocity,vId); gl.uniform1i(advP.uniforms.uSource,vId);
        gl.uniform1f(advP.uniforms.dt,dt); gl.uniform1f(advP.uniforms.dissipation,config.VELOCITY_DISSIPATION);
        blit(vel.write); vel.swap();
        gl.uniform2f(advP.uniforms.dyeTexelSize,dye.texelSizeX,dye.texelSizeY);
        gl.uniform1i(advP.uniforms.uVelocity,vel.read.attach(0));
        gl.uniform1i(advP.uniforms.uSource,dye.read.attach(1));
        gl.uniform1f(advP.uniforms.dissipation,config.DENSITY_DISSIPATION);
        blit(dye.write); dye.swap();
      }

      function render() {
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); gl.enable(gl.BLEND);
        dispM.bind();
        //@ts-ignore
        if (config.SHADING) gl.uniform2f(dispM.uniforms.texelSize,1/gl.drawingBufferWidth,1/gl.drawingBufferHeight);
        //@ts-ignore
        gl.uniform1i(dispM.uniforms.uTexture,dye.read.attach(0));
        blit(null);
      }

      /* ─────────────────── INIT & LOOP ─────────────────── */
      initFBOs();

      let lastTime=Date.now(), colorTimer=0;

      function frame() {
        const now=Date.now(), dt=Math.min((now-lastTime)/1000,0.016666); lastTime=now;
        if (resizeCanvas()) initFBOs();
        colorTimer += dt*config.COLOR_UPDATE_SPEED;
        //@ts-ignore
        if (colorTimer>=1) { colorTimer=0; pointers.forEach(p=>{p.color=generateFireColor();}); }
        
        //@ts-ignore
        pointers.forEach(p=>{if(p.moved){p.moved=false;splatPtr(p);}});
        step(dt); render();
        frameId = requestAnimationFrame(frame);
      }
      frameId = requestAnimationFrame(frame);

      /* ─────────────────── EVENTS ─────────────────── */
      function getPos(e: any, touch?: any) {
        const src=touch||e;
        return { x: scl(src.clientX), y: scl(src.clientY) };
      }

      const isIgnored = (target: HTMLElement | null) => {
        if (!target) return false;
        const selectors = [
          'nav', 'button', 'a', 'input', 'select', 'textarea', '.glass-panel',
          'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span', 'li', 'strong', 'em', 'label',
          'img', 'svg', 'video', 'picture', '.image-container', '.bg-cover', '.bg-contain',
          '[role="button"]', '[data-no-fire="true"]', '[data-no-fluid="true"]'
        ];
        if (selectors.some(s => target.closest(s))) return true;

        const style = window.getComputedStyle(target);
        
        // Check for background image
        if (style.backgroundImage && style.backgroundImage !== 'none') return true;

        const cursor = style.cursor;
        return cursor === 'pointer' || cursor === 'text' || cursor === 'crosshair' || cursor === 'grab' || cursor === 'grabbing';
      };
      
      const handleMouseMove = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const ignored = isIgnored(target);
        
        // Dynamic opacity adjustment
        if (canvas) {
          canvas.style.transition = ignored ? 'opacity 0s' : 'opacity 0.3s ease';
          canvas.style.opacity = ignored ? '0' : '0.9';
        }

        if (ignored) {
          //@ts-ignore
          pointers[0].moved = false;
          return;
        }
        const {x,y}=getPos(e); 
        //@ts-ignore
        const p=pointers[0];
        if (!p.color) p.color=generateFireColor();
        updateMove(p,x,y);
      };

      const handleMouseDown = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (isIgnored(target)) return;
        const {x,y}=getPos(e); 
        //@ts-ignore
        updateDown(pointers[0],-1,x,y);
        //@ts-ignore
        clickSplat(pointers[0]);
      };

      const handleMouseUp = () => { 
        //@ts-ignore
        pointers[0].down=false; 
      };

      const handleTouchStart = (e: TouchEvent) => {
        const target = e.target as HTMLElement;
        const ignored = isIgnored(target);

        if (canvas) {
          canvas.style.transition = ignored ? 'opacity 0s' : 'opacity 0.3s ease';
          canvas.style.opacity = ignored ? '0' : '0.9';
        }

        if (ignored) return;
        const t=e.changedTouches[0]; const {x,y}=getPos(e,t);
        //@ts-ignore
        updateDown(pointers[0],t.identifier,x,y); 
        //@ts-ignore
        clickSplat(pointers[0]);
      };

      const handleTouchMove = (e: TouchEvent) => {
        const target = e.target as HTMLElement;
        const ignored = isIgnored(target);

        if (canvas) {
          canvas.style.transition = ignored ? 'opacity 0s' : 'opacity 0.3s ease';
          canvas.style.opacity = ignored ? '0' : '0.9';
        }

        if (ignored) {
          //@ts-ignore
          pointers[0].moved = false;
          return;
        }
        const t = e.changedTouches[0];
        const {x,y}=getPos(e,t);
        //@ts-ignore
        updateMove(pointers[0],x,y);
      };

      const handleTouchEnd = () => { 
        //@ts-ignore
        pointers[0].down=false; 
        if (canvas) canvas.style.opacity = '0.9';
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mousedown', handleMouseDown);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchstart', handleTouchStart, {passive:true});
      window.addEventListener('touchmove', handleTouchMove, {passive:true});
      window.addEventListener('touchend', handleTouchEnd);

      return () => {
        cancelAnimationFrame(frameId);
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mousedown', handleMouseDown);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchstart', handleTouchStart);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
      };
    } catch (e) {
      console.error('FluidCursor error:', e);
    }
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none z-[1]">
      <canvas 
        ref={canvasRef} 
        id="fluid"
        className="w-full h-full mix-blend-screen"
      />
    </div>
  );
}
